import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  Breadcrumbs,
  type BreadcrumbItem,
} from '../../components/layout/Breadcrumbs';
import {
  createMerchantProgram,
  fetchMerchantOptions,
  fetchMerchantProgramById,
  updateMerchantProgram,
  type MerchantOption,
  type MerchantProgramPayload,
  type MerchantProgramType,
  type NewMerchantRegistrationConfig,
} from '../../services/merchantProgramsService';

const { TextArea } = Input;

const TYPE_OPTIONS: { value: MerchantProgramType; label: string }[] = [
  { value: 'fee_waiver', label: 'Miễn phí (không thu phí trên đơn)' },
  { value: 'fee_schedule', label: 'Lịch thu phí (sau ưu đãi)' },
  { value: 'announcement', label: 'Khuyến mãi / Thông báo riêng' },
  {
    value: 'new_merchant_registration',
    label: 'Merchant đăng ký mới (gói onboarding)',
  },
];

interface RegistrationPackageForm {
  name: string;
  price: number;
  description?: string;
  duration?: number;
  isPopular?: boolean;
}

interface FormValues {
  restaurantId?: number;
  programType: MerchantProgramType;
  name: string;
  description?: string;
  status?: 'scheduled' | 'active' | 'ended' | 'cancelled';
  range?: [dayjs.Dayjs, dayjs.Dayjs | null];
  feeType?: 'percent' | 'fixed';
  feePercent?: number;
  feeAmountVnd?: number;
  showBanner?: boolean;
  bannerThresholdDays?: number;
  bannerTitle?: string;
  bannerBody?: string;
  bannerDismissible?: boolean;
  freeRegistrationEnabled?: boolean;
  registrationPackages?: RegistrationPackageForm[];
}

const DEFAULT_REGISTRATION_PACKAGES: RegistrationPackageForm[] = [
  { name: 'Gói cơ bản', price: 200000, duration: 1 },
  { name: 'Gói nâng cao', price: 500000, duration: 1, isPopular: true },
];

export default function MerchantProgramFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [form] = Form.useForm<FormValues>();
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const programType = Form.useWatch('programType', form);
  const feeType = Form.useWatch('feeType', form);
  const isRegistrationProgram = programType === 'new_merchant_registration';

  const loadMerchants = useCallback(async () => {
    try {
      setMerchants(await fetchMerchantOptions());
    } catch {
      message.error('Không tải được danh sách quán');
    }
  }, []);

  const loadProgram = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetchMerchantProgramById(Number(id));
      if (res.success && res.data) {
        const p = res.data;
        const cfg = p.config as {
          fee_type?: 'percent' | 'fixed';
          fee_rate?: number;
          fee_amount_vnd?: number;
          free_registration_enabled?: boolean;
          packages?: Array<{
            name?: string;
            price?: number;
            description?: string;
            duration?: number;
            is_popular?: boolean;
          }>;
        };
        form.setFieldsValue({
          restaurantId: p.restaurant_id ?? undefined,
          programType: p.program_type,
          name: p.name,
          description: p.description ?? undefined,
          status: p.status,
          range: [
            p.starts_at ? dayjs(p.starts_at) : dayjs(),
            p.ends_at ? dayjs(p.ends_at) : null,
          ],
          feeType: cfg?.fee_type ?? 'percent',
          feePercent: cfg?.fee_rate != null ? cfg.fee_rate * 100 : undefined,
          feeAmountVnd: cfg?.fee_amount_vnd,
          showBanner: p.show_banner,
          bannerThresholdDays: p.banner_threshold_days,
          bannerTitle: p.banner_title ?? undefined,
          bannerBody: p.banner_body ?? undefined,
          bannerDismissible: p.banner_dismissible,
          freeRegistrationEnabled: cfg?.free_registration_enabled === true,
          registrationPackages:
            p.program_type === 'new_merchant_registration'
              ? (cfg?.packages ?? []).map(pkg => ({
                  name: pkg.name ?? '',
                  price: pkg.price ?? 0,
                  description: pkg.description,
                  duration: pkg.duration ?? 1,
                  isPopular: pkg.is_popular === true,
                }))
              : undefined,
        });
      } else {
        message.error(res.message || 'Không tải được chương trình');
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Lỗi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [id, form]);

  useEffect(() => {
    loadMerchants();
  }, [loadMerchants]);

  useEffect(() => {
    if (isEdit) {
      loadProgram();
    } else {
      form.setFieldsValue({
        programType: 'fee_waiver',
        status: 'active',
        range: [dayjs(), dayjs().add(3, 'month')],
        feeType: 'percent',
        showBanner: true,
        bannerThresholdDays: 7,
        bannerDismissible: true,
      });
    }
  }, [isEdit, loadProgram, form]);

  useEffect(() => {
    if (isEdit || programType !== 'new_merchant_registration') return;
    const current = form.getFieldValue('registrationPackages') as
      | RegistrationPackageForm[]
      | undefined;
    if (!current?.length) {
      form.setFieldsValue({
        registrationPackages: DEFAULT_REGISTRATION_PACKAGES,
        freeRegistrationEnabled: false,
      });
    }
  }, [programType, isEdit, form]);

  const onFinish = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const [start, end] = values.range ?? [];
      const payload: MerchantProgramPayload = {
        programType: values.programType,
        name: values.name,
        description: values.description,
        status: values.status,
        startsAt: start ? start.toISOString() : undefined,
        endsAt: end ? end.toISOString() : undefined,
      };

      if (values.programType !== 'new_merchant_registration') {
        payload.restaurantId = values.restaurantId;
        payload.showBanner = values.showBanner;
        payload.bannerThresholdDays = values.bannerThresholdDays;
        payload.bannerTitle = values.bannerTitle;
        payload.bannerBody = values.bannerBody;
        payload.bannerDismissible = values.bannerDismissible;
      }

      if (values.programType === 'fee_schedule') {
        payload.config =
          values.feeType === 'fixed'
            ? {
                fee_type: 'fixed',
                fee_amount_vnd: Math.round(values.feeAmountVnd ?? 0),
              }
            : { fee_type: 'percent', fee_rate: (values.feePercent ?? 0) / 100 };
      }

      if (values.programType === 'new_merchant_registration') {
        const packages = (values.registrationPackages ?? []).map(pkg => ({
          name: pkg.name.trim(),
          price: Math.round(pkg.price ?? 0),
          description: pkg.description?.trim() || undefined,
          duration: 1,
          is_popular: pkg.isPopular === true,
        }));
        payload.config = {
          free_registration_enabled: values.freeRegistrationEnabled === true,
          packages,
        } satisfies NewMerchantRegistrationConfig;
      }

      const res = isEdit
        ? await updateMerchantProgram(Number(id), payload)
        : await createMerchantProgram(payload);

      if (res.success) {
        message.success(
          isEdit ? 'Đã cập nhật chương trình' : 'Đã tạo chương trình'
        );
        navigate('/merchant-programs');
      } else {
        message.error(res.message || 'Lưu thất bại');
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Lưu thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumbs
        items={
          [
            { label: 'Trang chủ', path: '/' },
            { label: 'Chương trình Merchant', path: '/merchant-programs' },
            { label: isEdit ? 'Chỉnh sửa' : 'Thêm mới' },
          ] satisfies BreadcrumbItem[]
        }
      />
      <h2 style={{ marginTop: 0 }}>
        {isEdit ? 'Chỉnh sửa chương trình' : 'Thêm chương trình Merchant'}
      </h2>

      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ maxWidth: 720 }}
        >
          <Form.Item
            name="programType"
            label="Loại chương trình"
            rules={[{ required: true }]}
          >
            <Select options={TYPE_OPTIONS} disabled={isEdit} />
          </Form.Item>

          {!isRegistrationProgram && (
            <Form.Item
              name="restaurantId"
              label="Quán (Merchant)"
              rules={[{ required: true, message: 'Chọn quán' }]}
            >
              <Select
                placeholder="Chọn quán"
                showSearch
                optionFilterProp="label"
                disabled={isEdit}
                options={merchants.map(m => ({
                  value: m.restaurantId,
                  label: m.label,
                }))}
              />
            </Form.Item>
          )}

          {isRegistrationProgram && (
            <Form.Item label="Phạm vi">
              <Input
                value="Toàn hệ thống — áp dụng cho mọi merchant đăng ký mới"
                disabled
              />
            </Form.Item>
          )}

          <Form.Item
            name="name"
            label="Tên chương trình"
            rules={[{ required: true, message: 'Nhập tên chương trình' }]}
          >
            <Input
              placeholder={
                isRegistrationProgram
                  ? 'VD: Gói đăng ký Q3/2026'
                  : 'VD: Miễn phí 3 tháng'
              }
            />
          </Form.Item>

          <Form.Item name="description" label="Mô tả">
            <TextArea rows={2} placeholder="Mô tả ngắn (tùy chọn)" />
          </Form.Item>

          <Form.Item
            name="range"
            label="Thời gian áp dụng (bắt đầu → kết thúc)"
            rules={[
              {
                validator: (
                  _,
                  value: [dayjs.Dayjs, dayjs.Dayjs | null] | undefined
                ) =>
                  value && value[0]
                    ? Promise.resolve()
                    : Promise.reject(new Error('Chọn thời gian bắt đầu')),
              },
            ]}
            extra={
              isRegistrationProgram
                ? 'Trong khoảng thời gian này, gói đăng ký sẽ áp dụng cho merchant mới. Bật "Đăng ký miễn phí" để giá hiển thị 0đ.'
                : 'Để trống ngày kết thúc nếu không giới hạn (áp dụng cho lịch thu phí).'
            }
          >
            <DatePicker.RangePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              allowEmpty={[false, true]}
              style={{ width: '100%' }}
            />
          </Form.Item>

          {programType === 'fee_schedule' && (
            <>
              <Divider orientation="left">Cấu hình phí</Divider>
              <Form.Item
                name="feeType"
                label="Kiểu thu phí"
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Radio value="percent">Theo phần trăm (%)</Radio>
                  <Radio value="fixed">Số tiền cố định / đơn</Radio>
                </Radio.Group>
              </Form.Item>

              {feeType === 'fixed' ? (
                <Form.Item
                  name="feeAmountVnd"
                  label="Số tiền phí mỗi đơn (VND)"
                  rules={[{ required: true, message: 'Nhập số tiền phí' }]}
                >
                  <InputNumber
                    min={0}
                    step={500}
                    style={{ width: '100%' }}
                    formatter={v =>
                      `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={(v: string | undefined): number =>
                      Number((v ?? '').replace(/,/g, ''))
                    }
                    addonAfter="đ/đơn"
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="feePercent"
                  label="Phần trăm phí trên đơn"
                  rules={[{ required: true, message: 'Nhập % phí' }]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.1}
                    style={{ width: '100%' }}
                    addonAfter="%"
                  />
                </Form.Item>
              )}
            </>
          )}

          {isRegistrationProgram && (
            <>
              <Divider orientation="left">Gói đăng ký merchant mới</Divider>

              <Form.Item
                name="freeRegistrationEnabled"
                label="Đăng ký miễn phí trong thời gian chương trình"
                valuePropName="checked"
                extra="Khi bật, merchant mới thấy giá 0đ cho đến khi chương trình kết thúc (theo ngày kết thúc ở trên)."
              >
                <Switch />
              </Form.Item>

              <Form.List
                name="registrationPackages"
                rules={[
                  {
                    validator: async (_, pkgs) => {
                      if (!pkgs || pkgs.length < 1) {
                        return Promise.reject(
                          new Error('Cần ít nhất 1 gói đăng ký')
                        );
                      }
                    },
                  },
                ]}
              >
                {(fields, { add, remove }, { errors }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card
                        key={key}
                        size="small"
                        style={{ marginBottom: 12 }}
                        title={`Gói #${name + 1}`}
                        extra={
                          fields.length > 1 ? (
                            <Button
                              type="text"
                              danger
                              icon={<MinusCircleOutlined />}
                              onClick={() => remove(name)}
                            />
                          ) : null
                        }
                      >
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          label="Tên gói"
                          rules={[{ required: true, message: 'Nhập tên gói' }]}
                        >
                          <Input placeholder="VD: Gói cơ bản" />
                        </Form.Item>
                        <Space align="start" style={{ width: '100%' }} wrap>
                          <Form.Item
                            {...restField}
                            name={[name, 'price']}
                            label="Giá (VND)"
                            rules={[
                              { required: true, message: 'Nhập giá gói' },
                            ]}
                          >
                            <InputNumber
                              min={0}
                              step={10000}
                              style={{ width: 200 }}
                              formatter={v =>
                                `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                              }
                              parser={(v: string | undefined): number =>
                                Number((v ?? '').replace(/,/g, ''))
                              }
                              addonAfter="đ"
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'isPopular']}
                            label="Gói nổi bật"
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </Space>
                        <Form.Item
                          {...restField}
                          name={[name, 'description']}
                          label="Mô tả"
                        >
                          <TextArea
                            rows={2}
                            placeholder="Mô tả gói (tùy chọn)"
                          />
                        </Form.Item>
                      </Card>
                    ))}
                    <Form.ErrorList errors={errors} />
                    <Button
                      type="dashed"
                      onClick={() => add({ name: '', price: 0 })}
                      block
                      icon={<PlusOutlined />}
                    >
                      Thêm gói
                    </Button>
                  </>
                )}
              </Form.List>
            </>
          )}

          {!isRegistrationProgram && (
            <>
              <Divider orientation="left">Banner trên app Merchant</Divider>

              <Form.Item
                name="showBanner"
                label="Hiển thị banner"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="bannerThresholdDays"
                label="Ngưỡng luôn hiển thị (số ngày còn lại)"
                extra="Khi thời gian còn lại ≤ ngưỡng này, banner luôn hiển thị (không thể tắt)."
              >
                <InputNumber
                  min={0}
                  max={365}
                  style={{ width: '100%' }}
                  addonAfter="ngày"
                />
              </Form.Item>

              <Form.Item name="bannerTitle" label="Tiêu đề banner">
                <Input placeholder="VD: Ưu đãi miễn phí 3 tháng" />
              </Form.Item>

              <Form.Item name="bannerBody" label="Nội dung banner">
                <TextArea
                  rows={2}
                  placeholder="VD: Phí trên đơn sẽ áp dụng khi chương trình kết thúc."
                />
              </Form.Item>

              <Form.Item
                name="bannerDismissible"
                label="Cho phép tắt (X)"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </>
          )}

          {isEdit && (
            <Form.Item name="status" label="Trạng thái">
              <Select
                options={[
                  { value: 'scheduled', label: 'Lên lịch' },
                  { value: 'active', label: 'Đang chạy' },
                  { value: 'ended', label: 'Đã kết thúc' },
                  { value: 'cancelled', label: 'Đã hủy' },
                ]}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {isEdit ? 'Cập nhật' : 'Tạo chương trình'}
              </Button>
              <Button onClick={() => navigate('/merchant-programs')}>
                Hủy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
